import React, { useState, useRef, useEffect } from 'react';
import { Button, Stack, Alert, Spinner } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { uploadFile } from 'service';
import useFetch from 'use-http';
import {useAuth0} from "@ondeck/silkworm";

interface IFile extends File {
  message?: string;
  loading?: boolean;
}

let acceptedFileTypes = {
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
    '.docx'
  ],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
    '.xlsx'
  ],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/gif': ['.gif'],
  'image/png': ['.png'],
  'text/plain': ['.txt']
};

interface FileUploadProps {
  businessId: string;
  disableSelectFileButton: boolean;
}

const FileUpload = ({ businessId, disableSelectFileButton }: FileUploadProps) => {
  const inputFileRef = useRef<HTMLInputElement>(null);
  const { formatMessage } = useIntl();
  const intl = useIntl();
  const [selectedFiles, setSelectedFiles] = useState<IFile[]>([]);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [inputFileKey, setInputFileKey] = useState(Date.now());
  const [uploadStatusSuccessful, setUploadStatusSuccessful] = useState(false);
  const [reviewedDocuments, setReviewedDocuments] = useState<any[]>([]);
  const { isAuthenticated, getTokenSilently } = useAuth0();
  const [pendingUpload, setPendingUpload] = useState(false);

  const { user } = useAuth0();

  const hook = useFetch(window.env.REACT_APP_PARTNER_ORCHESTRATOR, {
    cacheLife: 5000,
    retryDelay: 30000,
    headers: {
      partnerContactEmail: `${user.email}`
    },
    interceptors: {
      request: async ({ options }: any) => {
        if (isAuthenticated) {
          options.headers.Authorization = `Bearer ${await getTokenSilently()}`;
        }

        return options;
      }
    }
  });

  const validateFiles = (filesArray: IFile[]): boolean => {
    var totalFileSize = 0;
    var errorCount = 0;
    var uploadErrorMessages: string[] = [];
    filesArray.forEach(file => {
      file.message = '';
      if (file.size / 1024 / 1024 > 30) {
        file.message += file.message += formatMessage({
          id: 'closingDocs.fileSize'
        });
        errorCount++;
      }
      totalFileSize += file.size / 1024 / 1024;
      if (!acceptedFileTypes[file.type]) {
        file.message += formatMessage({
          id: 'closingDocs.unsupportedFileType'
        });
        errorCount++;
      }
    });
    if (totalFileSize > 150) {
      uploadErrorMessages.push(
        formatMessage({ id: 'closingDocuments.totalFileSize' })
      );
      errorCount++;
    }
    if (errorCount > 0) {
      uploadErrorMessages.push(
        formatMessage({ id: 'closingDocs.errorsFound' })
      );
      errorCount++;
    }
    setErrorMessages(uploadErrorMessages);
    return errorCount === 0;
  };

  const processInputFiles = (event: any) => {
    var filesArray: IFile[] = Array.from(event.target.files);

    // Append only new files
    setSelectedFiles(
      selectedFiles.concat(
        filesArray.filter(incomingFile => {
          return !selectedFiles.some(currentFile => {
            return incomingFile.name === currentFile.name;
          });
        })
      )
    );
  };

  const removeSelected = (fileNames: string | string[]) => {
    const filteredFiles = selectedFiles.filter(item => !fileNames.includes(item.name));
    setSelectedFiles(filteredFiles);
    // reset the dom input
    setInputFileKey(Date.now);
  };

  const uploadFiles = async () => {
    setPendingUpload(true);
    setReviewedDocuments([]);
    var validFiles = validateFiles(selectedFiles);
    if (validFiles) {
      await (async () => {
        const response = await uploadFile(hook, businessId, selectedFiles);
        if (response.status) {
          setUploadStatusSuccessful(false);
          setErrorMessages([
            formatMessage({ id: 'closingDocs.serverUploadError' })
          ]);
        } else {
          setUploadStatusSuccessful(true);
          setReviewedDocuments(response.statuses);
        }
      })();
    }
    setPendingUpload(false);
  };

  useEffect(() => {
    validateFiles(selectedFiles)
  }, [selectedFiles]);

  useEffect(() => {
    let filesToRemove:string[] = [];
    reviewedDocuments.forEach(doc => {
      if (doc.uploaded) {
        filesToRemove.push(doc.fileName)
      }
    });
    removeSelected(filesToRemove);
  }, [reviewedDocuments]);
  
  const renderFileResultMessage = (index: number, doc: any) => {
    return (
      <span key={index}className={'fs-5 p-2 ' + (!doc.uploaded ? 'alert-danger' : 'alert-info')}>
        {doc.fileName}
        <FormattedMessage id={`fileUpload.${doc.uploaded ? 'uploadFileSuccess' : 'uploadFileError'}`} />
      </span>
    );
  };

  return (
    <Stack gap={3} className="row m-0">
      <Button className="col-md-3 px-1 position-relative overflow-hidden"
              disabled={disableSelectFileButton}>
        <FormattedMessage id="fileUpload.addFile" />
        <input
          accept={Object.values(acceptedFileTypes).join(',')}
          type="file"
          multiple
          onChange={processInputFiles}
          ref={inputFileRef}
          key={inputFileKey}
          aria-label="inputFiles"
          className="d-block position-absolute top-0 end-0 opacity-0"
          style={{ minHeight: 100, minWidth: 100 }}
        />
      </Button>
      { selectedFiles.length !== 0 &&
        <div className="p-3 bg-white border border-2 border-secondary">
          <div className="fs-3 mb-1">
            <FormattedMessage id="fileUpload.fileQueue" />
          </div>
          {selectedFiles &&
            selectedFiles.map((file, index) => (
              <Stack className="fs-5 my-2" key={index}>
                <Stack direction="horizontal" gap={1}>
                  <span>{file.name}</span>
                  <Button
                    variant="link"
                    className="fs-5 fw-500 text-decoration-none p-0"
                    onClick={() => removeSelected(file.name)}
                  >
                    <FormattedMessage id="fileUpload.remove" />
                  </Button>
                </Stack>
                <span className="text-danger">{file.message}</span>
              </Stack>
            ))}
          <Button
            className="fs-3 mt-2 col-md-4 col-12 px-1"
            disabled={
              !selectedFiles ||
              selectedFiles.length === 0 ||
              errorMessages?.length > 0 ||
              pendingUpload
            }
            onClick={uploadFiles}
          >
            { pendingUpload ?
              <Stack gap={2} direction="horizontal" className="ms-4">
                <Spinner animation="border" variant="light" size="sm"/>
                <FormattedMessage id="fileUpload.uploading"/>
              </Stack>
              :
              <FormattedMessage id="fileUpload.upload" />
            }
          </Button>
        </div>
      }

      {uploadStatusSuccessful && reviewedDocuments.length > 0 && (
        <Stack className="p-0">
          {reviewedDocuments.map((doc, index) =>
            renderFileResultMessage(index, doc)
          )}
        </Stack>
      )}

      {errorMessages?.length > 0 &&
        selectedFiles.filter(file => file.message != null).length > 0 && (
          <Alert variant="danger">
            <Stack gap={1}>
              {errorMessages.map((errorMessage, index) => (
                <span key={index}>{errorMessage}</span>
              ))}
            </Stack>
          </Alert>
        )}
    </Stack>
  );
};

export default FileUpload;
